<template>
  <form
    class="contact-form flex flex-col !p-0 max-h-[90vh]"
    @submit.prevent="handleSubmit"
    @keydown="handleFormKeydown"
  >
    <woot-modal-header-v2 :header-title="headerTitle">
      <template #titleIcon>
        <v2-icon icon="user_card" class="text-gray-500" />
      </template>
    </woot-modal-header-v2>

    <div class="px-6 py-4 flex flex-col overflow-y-auto flex-1 gap-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-sm leading-5 text-gray-800">
          {{ $t('CONTACT_FORM.FORM.AVATAR.TITLE') }}
        </label>
        <div class="flex items-center gap-6">
          <div
            class="w-[64px] h-[64px] rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden"
          >
            <img
              v-if="avatarUrl"
              :src="avatarUrl"
              class="w-full h-full object-cover"
            />
            <v2-icon v-else icon="user_card" />
          </div>
          <div class="flex flex-col gap-1.5">
            <button-v2
              size="sm"
              variant="ghost"
              icon="upload"
              @click="triggerFileUpload"
            >
              {{ $t('CONTACT_FORM.FORM.AVATAR.UPLOAD') }}
            </button-v2>
            <button-v2
              size="sm"
              variant="ghost"
              icon="delete"
              color="danger"
              :disabled="!avatarUrl"
              @click="handleAvatarDelete"
            >
              {{ $t('CONTACT_FORM.FORM.AVATAR.DELETE') }}
            </button-v2>
          </div>
          <input
            ref="avatarInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="onAvatarFileChange"
          />
        </div>
      </div>

      <input-v2
        v-model="name"
        required
        :dir="name ? 'auto' : null"
        :label="$t('CONTACT_FORM.FORM.NAME.LABEL')"
        :placeholder="$t('CONTACT_FORM.FORM.NAME.FORM_PLACEHOLDER')"
        :error="$v.name.$error"
        @input="$v.name.$touch"
      />

      <div class="flex flex-col gap-2">
        <div class="grid grid-cols-2 gap-3">
          <input-v2
            v-model="email"
            :dir="email ? 'auto' : null"
            :label="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.SHORT_LABEL')"
            :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
            :error="
              $v.email.$error ? $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') : ''
            "
            @input="$v.email.$touch"
          />
          <phone-input-v2
            v-model="phoneNumber"
            :label="$t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL')"
            placeholder="XXX-XXXXXX"
            :error="
              $v.phoneNumber.$error
                ? $t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR')
                : ''
            "
            @input="$v.phoneNumber.$touch"
          />
        </div>

        <span
          class="flex items-center gap-1 text-sm leading-5"
          :class="showEmailOrPhoneError ? 'text-danger-700' : 'text-gray-600'"
        >
          <v2-icon icon="info" :size="16" class="shrink-0" />
          {{ $t('CONTACT_FORM.FORM.EMAIL_OR_PHONE_REQUIRED') }}
        </span>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm text-gray-700">
            {{ $t('CONTACT_FORM.FORM.BIRTHDAY.LABEL') }}
          </label>
          <date-picker
            ref="birthdayPicker"
            v-model="birthday"
            class="birthdayDataPicker"
            format="DD-MM-YYYY"
            type="date"
            :confirm="true"
            :lang="calendarLang"
            :placeholder="$t('CONTACT_FORM.FORM.BIRTHDAY.PLACEHOLDER')"
            @open="onBirthdayPickerOpen"
            @close="onBirthdayPickerClose"
          >
            <template #footer>
              <div
                class="flex flex-row justify-end gap-2 custom-datepicker-footer"
              >
                <woot-button
                  class="button clear"
                  color-scheme="secondary"
                  size="small"
                  @click="cancelBirthday"
                >
                  {{ $t('FILTER.CANCEL_BUTTON_LABEL') }}
                </woot-button>
                <woot-button
                  color-scheme=""
                  class="!bg-green-500"
                  size="small"
                  @click="confirmBirthday"
                >
                  {{ $t('FILTER.APPLY_BUTTON_LABEL') }}
                </woot-button>
              </div>
            </template>
            <template #icon-calendar>
              <v2-icon icon="calendar_month" size="24" class="text-gray-500" />
            </template>
          </date-picker>
        </div>
        <input-v2
          v-model="companyName"
          :dir="companyName ? 'auto' : null"
          :label="$t('CONTACT_FORM.FORM.COMPANY_NAME.LABEL')"
          :placeholder="$t('CONTACT_FORM.FORM.COMPANY_NAME.PLACEHOLDER')"
        />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm text-gray-700">
            {{ $t('CONTACT_FORM.FORM.COUNTRY.LABEL') }}
          </label>
          <woot-multiselect
            v-model="country"
            track-by="id"
            label="name"
            class="no-margin"
            :class="{ 'country-select--unset': !country || !country.id }"
            :placeholder="$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')"
            :search-placeholder="$t('FILTER.SEARCH_PLACEHOLDER')"
            selected-label
            :select-label="$t('CONTACT_FORM.FORM.COUNTRY.SELECT_PLACEHOLDER')"
            :deselect-label="$t('CONTACT_FORM.FORM.COUNTRY.REMOVE')"
            :custom-label="countryNameWithCode"
            :max-height="200"
            :options="countries"
            :allow-empty="true"
            :option-height="104"
          >
            <template #caret="{ toggle }">
              <span
                class="country-select__caret"
                @mousedown.prevent.stop="toggle"
              >
                <v2-icon icon="keyboard_arrow_down" size="24" />
              </span>
            </template>
          </woot-multiselect>
        </div>
        <input-v2
          v-model="city"
          :dir="city ? 'auto' : null"
          :label="$t('CONTACT_FORM.FORM.CITY.LABEL')"
          :placeholder="$t('CONTACT_FORM.FORM.CITY.PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-sm text-gray-700">
          {{ $t('CONTACT_FORM.FORM.BIO.LABEL') }}
        </label>
        <textarea
          v-model.trim="description"
          :dir="description ? 'auto' : null"
          :placeholder="$t('CONTACT_FORM.FORM.BIO.PLACEHOLDER')"
          maxlength="5000"
          rows="1"
          class="w-full !h-11 !min-h-0 box-border px-3 py-2 mb-0 rounded-lg border border-gray-300 dark:border-slate-600 text-base leading-6 text-gray-800 dark:text-slate-100 placeholder-gray-400 bg-white dark:bg-slate-900 outline-none hover:border-gray-400 focus:border-gray-400 transition-colors resize-none"
          @input="$v.description.$touch"
        />
        <div
          class="text-right rtl:text-left text-sm leading-5 text-gray-700 dark:text-slate-400"
        >
          {{ (description || '').length }}/5000
        </div>
      </div>

      <hr class="w-full border-b border-solid border-[#DFDCDC] my-0" />

      <div class="flex flex-col gap-4">
        <p class="!text-base !leading-6 font-normal !text-[#000000] !mt-0">
          {{ $t('CONTACT_FORM.FORM.SOCIAL_PROFILES.TITLE') }}
        </p>
        <div class="flex flex-col gap-3">
          <input-v2
            v-for="socialProfile in socialProfileKeys"
            :key="socialProfile.key"
            v-model="socialProfileUserNames[socialProfile.key]"
            :dir="socialProfileUserNames[socialProfile.key] ? 'ltr' : null"
            :placeholder="$t('CONTACT_FORM.FORM.SOCIAL_PROFILES.PLACEHOLDER')"
          >
            <template #prefix>
              <span
                class="flex items-center self-stretch px-3 w-[180px] shrink-0 whitespace-nowrap bg-gray-100 text-sm text-gray-700 border-e border-gray-300"
              >
                <bdi dir="ltr">{{ socialProfile.prefixURL }}</bdi>
              </span>
            </template>
          </input-v2>
        </div>
      </div>
    </div>

    <modal-footer-v2
      :cancel-text="$t('CONTACT_FORM.FORM.CANCEL')"
      :confirm-text="submitLabel"
      :is-disabled="inProgress || isCreateButtonDisabled"
      :is-loading="inProgress"
      confirm-type="submit"
      @cancel="onCancel"
    />
  </form>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import DatePicker from 'vue2-datepicker';
import parsePhoneNumber from 'libphonenumber-js';
import V2Icon from 'v2/components/Icon.vue';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { required, email } from 'vuelidate/lib/validators';
import {
  isContactPhoneValid,
  isValidPhoneStart,
} from 'shared/helpers/Validators';
import countries from 'shared/constants/countries.js';
import {
  closeDatePickerOnOutsideClick,
  datePickerLang,
} from 'dashboard/helper/datePickerHelper';

const normalisePhoneNumber = (value = '') =>
  value.replace(/^05/, '+9725').replace(/^\+9720(\d*)$/, '+972$1');

const isDialCodeOnly = value =>
  countries.some(country => country.dial_code === value);

export default {
  components: {
    DatePicker,
    V2Icon,
  },
  mixins: [alertMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    inProgress: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      countries: countries,
      birthday: '',
      companyName: '',
      description: '',
      email: '',
      name: '',
      phoneNumber: '',
      // activeDialCode: '', // d99d remarked out
      avatarFile: null,
      avatarUrl: '',
      country: {
        id: '',
        name: '',
      },
      city: '',
      socialProfileUserNames: {
        facebook: '',
        twitter: '',
        linkedin: '',
        instagram: '',
      },
      socialProfileKeys: [
        { key: 'facebook', prefixURL: 'https://facebook.com/' },
        { key: 'twitter', prefixURL: 'https://twitter.com/' },
        { key: 'linkedin', prefixURL: 'https://linkedin.com/' },
        { key: 'instagram', prefixURL: 'https://instagram.com/' },
      ],
    };
  },
  validations: {
    name: {
      required,
    },
    description: {},
    email: {
      email,
    },
    companyName: {},
    phoneNumber: {
      isContactPhoneValid: value =>
        isDialCodeOnly(value) ||
        isContactPhoneValid(normalisePhoneNumber(value)),
      isValidPhoneStart: value =>
        isValidPhoneStart(normalisePhoneNumber(value)),
    },
    bio: {},
  },
  computed: {
    headerTitle() {
      if (this.contact && this.contact.id) {
        return `${this.$t('EDIT_CONTACT.TITLE')} - ${
          this.contact.name || this.contact.email || ''
        }`;
      }
      return this.$t('CREATE_CONTACT.TITLE');
    },
    isEditMode() {
      return !!(this.contact && this.contact.id);
    },
    calendarLang() {
      return datePickerLang(this.$i18n.locale);
    },
    hasPhoneNumber() {
      return !!parsePhoneNumber(this.phoneNumber || '')?.nationalNumber;
    },
    isEmailOrPhoneMissing() {
      return !this.isEditMode && !this.email.trim() && !this.hasPhoneNumber;
    },
    showEmailOrPhoneError() {
      return (
        this.isEmailOrPhoneMissing &&
        (!!this.name.trim() ||
          this.$v.email.$dirty ||
          this.$v.phoneNumber.$dirty)
      );
    },
    isCreateButtonDisabled() {
      if (this.isEditMode) return false;
      return !this.name.trim() || this.isEmailOrPhoneMissing;
    },
    submitLabel() {
      return this.contact && this.contact.id
        ? this.$t('CONTACT_FORM.FORM.SAVE')
        : this.$t('CONTACT_FORM.FORM.CREATE');
    },
  },
  watch: {
    contact() {
      this.setContactObject();
    },
  },
  mounted() {
    this.setFocusToFirstInput();
    this.setContactObject();
    // this.setDialCode(); // d99d remarked out
  },
  beforeDestroy() {
    this.onBirthdayPickerClose();
  },
  methods: {
    handleFormKeydown(event) {
      if (event.key === 'Enter') {
        if (document.activeElement.type !== 'submit') {
          event.preventDefault();
        }
      }
    },
    setFocusToFirstInput() {
      this.$nextTick(() => {
        const submitButton = this.$el.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.setAttribute('tabindex', '-1');
          submitButton.focus();
        }
      });
    },
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    countryNameWithCode({ name, id }) {
      if (!id) return name;
      if (!name && !id) return '';
      return `${name} (${id})`;
    },
    /* d99d remarked out
    setDialCode() {
      if (
        this.phoneNumber !== '' &&
        this.parsePhoneNumber &&
        this.parsePhoneNumber.countryCallingCode
      ) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        this.activeDialCode = `+${dialCode}`;
      }
    },
     */
    setContactObject() {
      const {
        email: emailAddress,
        phone_number: phoneNumber,
        name,
      } = this.contact;
      const additionalAttributes = this.contact.additional_attributes || {};

      this.name = name || '';
      this.email = emailAddress || '';
      this.phoneNumber = phoneNumber || '';
      this.birthday = this.setBirthday(additionalAttributes);
      this.companyName = additionalAttributes.company_name || '';
      this.country = {
        id: additionalAttributes.country_code || '',
        name:
          additionalAttributes.country ||
          this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY'),
      };
      this.city = additionalAttributes.city || '';
      this.description = additionalAttributes.description || '';
      this.avatarUrl = this.contact.thumbnail || '';
      const {
        social_profiles: socialProfiles = {},
        screen_name: twitterScreenName,
      } = additionalAttributes;
      this.socialProfileUserNames = {
        twitter: socialProfiles.twitter || twitterScreenName || '',
        facebook: socialProfiles.facebook || '',
        linkedin: socialProfiles.linkedin || '',
        github: socialProfiles.github || '',
        instagram: socialProfiles.instagram || '',
      };
    },
    getContactObject() {
      if (this.country === null) {
        this.country = {
          id: '',
          name: '',
        };
      }
      const contactObject = {
        id: this.contact.id,
        name: this.name,
        email: this.email,
        phone_number: isDialCodeOnly(this.phoneNumber) ? '' : this.phoneNumber,
        // wa_id is intentionally omitted - it is set by the whatsapp integration, not by agents
        // phone_number: this.setPhoneNumber, // d99d remarked out
        additional_attributes: {
          ...this.contact.additional_attributes,
          description: this.description,
          birthday:
            this.birthday instanceof Date
              ? this.birthday.toISOString()
              : this.birthday,
          company_name: this.companyName,
          country_code: this.country.id,
          country:
            this.country.name ===
            this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')
              ? ''
              : this.country.name,
          city: this.city,
          social_profiles: this.socialProfileUserNames,
        },
      };
      if (this.avatarFile) {
        contactObject.avatar = this.avatarFile;
        contactObject.isFormData = true;
      }
      return contactObject;
    },
    onPhoneNumberInputChange(value, code) {
      this.activeDialCode = code;
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    setBirthday(additionalAttributes) {
      if (!additionalAttributes.birthday) return '';
      const bday = additionalAttributes.birthday;
      const date = bday instanceof Date ? bday : new Date(bday);
      if (Number.isNaN(date.getTime())) return '';
      return date;
    },
    async handleSubmit() {
      if (this.isCreateButtonDisabled) return;
      this.phoneNumber = normalisePhoneNumber(this.phoneNumber);
      this.$v.$touch();

      if (
        this.$v.$invalid /* || this.isPhoneNumberNotValid d99d remarked out */
      ) {
        return;
      }
      try {
        await this.onSubmit(this.getContactObject());
        this.onSuccess();
        this.showAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          if (error.data.includes('email')) {
            this.showAlert(
              this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE')
            );
          } else if (error.data.includes('phone_number')) {
            this.showAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    triggerFileUpload() {
      this.$refs.avatarInput.click();
    },
    confirmBirthday() {
      if (this.$refs.birthdayPicker) {
        this.$refs.birthdayPicker.handleConfirmDate();
      }
    },
    cancelBirthday() {
      if (this.$refs.birthdayPicker) {
        this.$refs.birthdayPicker.closePopup();
      }
    },
    onBirthdayPickerOpen() {
      this.onBirthdayPickerClose();
      this.stopBirthdayOutsideClick = closeDatePickerOnOutsideClick(
        this.$refs.birthdayPicker
      );
    },
    onBirthdayPickerClose() {
      const picker = this.$refs.birthdayPicker;
      if (picker) picker.currentValue = picker.innerValue;
      if (this.stopBirthdayOutsideClick) {
        this.stopBirthdayOutsideClick();
        this.stopBirthdayOutsideClick = null;
      }
    },
    onAvatarFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.avatarFile = file;
        this.avatarUrl = URL.createObjectURL(file);
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        if (this.contact && this.contact.id) {
          await this.$store.dispatch('contacts/deleteAvatar', this.contact.id);
          this.showAlert(
            this.$t('CONTACT_FORM.DELETE_AVATAR.API.SUCCESS_MESSAGE')
          );
        }
        this.avatarFile = null;
        this.avatarUrl = '';
        // this.activeDialCode = ''; // d99d remarked out
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('CONTACT_FORM.DELETE_AVATAR.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.contact-form__input {
  @apply h-11 w-full px-3 py-2 rounded-lg border text-base text-gray-800 dark:text-slate-100 placeholder-gray-400 bg-white dark:bg-slate-900 outline-none transition-colors;
  &:focus {
    @apply ring-2 ring-[#2abb7f];
  }
}

::v-deep {
  .multiselect {
    .country-select__caret {
      @apply absolute top-1/2 flex items-center justify-center text-gray-600;
      inset-inline-end: 12px;
      transform: translateY(-50%);
      cursor: pointer;
      transition: transform 0.15s ease;
    }
    &.multiselect--active .country-select__caret {
      transform: translateY(-50%) scaleY(-1);
    }

    .multiselect__content-wrapper {
      overflow-x: hidden;
    }
    .multiselect__content {
      display: block;
      width: 100%;
      max-width: 100%;
      padding-top: 8px;
    }
    .multiselect__option {
      max-width: 100%;
      overflow: hidden;

      span {
        display: block;
        flex: 1 1 auto;
        min-width: 0;
        width: auto;
        max-width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }
}

.contact-form ::v-deep .country-select--unset.multiselect {
  .multiselect__single,
  .multiselect__tags .multiselect__placeholder {
    @apply text-gray-700;
  }
}
.birthdayDataPicker {
  width: 100% !important;
  ::v-deep input {
    @apply h-11 w-full px-3 py-2 mb-0 rounded-lg border border-gray-300 dark:border-slate-600 text-base text-gray-800 dark:text-slate-100 placeholder-gray-400 bg-white dark:bg-slate-900;
  }
}

label {
  font-weight: 400;
}

.multiselect {
  margin-bottom: 0;
}
</style>
